import { Store } from 'vuex';
import ReservationStep from '@/types/reservationStep';
import { debounce } from '@/utilities/helpers';

/**
 * Captures abandoned-cart lead data (first name / last name / email + step
 * progress) as the customer moves through the reservation wizard, and sends
 * one last best-effort save if they close/navigate away mid-flow. All of the
 * actual gating (valid email required, step -> abandonment stage mapping) is
 * done in `reservation/saveAbandonedCart` - this plugin only decides *when*
 * to fire it, the same way `analytics-vuex.ts` drives analytics off of the
 * same mutations.
 *
 * Gated entirely on the location's `abandoned_cart_capture` setting (exposed
 * via `location/settings`, populated synchronously before the store/plugins
 * are constructed - see `store/location.ts`). When it's off, this plugin does
 * nothing at all: no subscription, no pagehide listener, no dispatch, no
 * network call - a location that hasn't opted in should show zero evidence,
 * on the wire or otherwise, that this feature exists. Do not move this check
 * inside `saveAbandonedCart` alone; the point is to never even attempt the
 * call client-side, not just have the server reject it.
 */
const abandonedCartPlugin = (store: Store<any>) => {
	if (!store.getters['location/settings'].abandoned_cart_capture) {
		return;
	}

	let previousStep: ReservationStep = store.state.reservation.step;

	// Keystroke-driven saves (typing into the customer details form) are
	// debounced so we're not saving on every keypress; step transitions save
	// immediately since they're discrete, infrequent events.
	const saveDebounced = debounce(() => {
		store.dispatch('reservation/saveAbandonedCart');
	}, 600);

	store.subscribe((mutation) => {
		switch (mutation.type) {
			case 'reservation/setCustomer':
				saveDebounced();
				break;
			case 'reservation/setStep': {
				const lastCompletedStep = previousStep;
				previousStep = mutation.payload;

				// Confirmation is the post-booking receipt page - both booking
				// components move to it the instant saveReservation() resolves,
				// i.e. just after netPark deleted this cart's row. Capturing
				// here would resurrect it (the same reason the pagehide handler
				// below skips this step). `reservation/saveAbandonedCart` also
				// refuses once the cart is booked; this just avoids the
				// pointless dispatch.
				if (mutation.payload === ReservationStep.Confirmation) {
					break;
				}

				store.dispatch('reservation/saveAbandonedCart', { lastCompletedStep });
				break;
			}
			default:
				break;
		}
	});

	// Especially important to cover the "they're about to leave" case - a
	// normal axios request can get cancelled mid-flight when the page
	// unloads, so this uses sendBeacon instead (see AbandonedCartService).
	window.addEventListener('pagehide', () => {
		if (store.state.reservation.step === ReservationStep.Confirmation) {
			return;
		}

		store.dispatch('reservation/saveAbandonedCart', {
			lastCompletedStep: previousStep,
			beacon: true,
		});
	});
};

export default abandonedCartPlugin;
